import {
	BN,
	BigNum,
	DriftClient,
	MAX_LEVERAGE_ORDER_SIZE,
	MakerInfo,
	MarketType,
	OptionalOrderParams,
	OrderTriggerCondition,
	PositionDirection,
	PublicKey,
	ReferrerInfo,
	SpotMarketAccount,
	TxParams,
	User,
	UserAccount,
	getTriggerMarketOrderParams,
	isVariant,
	ZERO,
	OrderType,
	PerpMarketAccount,
} from '@drift-labs/sdk';
import {
	MarketId,
	UIOrderType,
	TradeOffsetPrice,
	UIMarket,
	OptionalAuctionParamsRequestInputs,
	OptionalTriggerOrderParams,
	AuctionParams,
	COMMON_UI_UTILS,
} from '@drift/common';
import { UserSettings } from '../../../../environmentVariables/EnvironmentVariableTypes';
import {
	DEFAULT_MARKET_AUCTION_DURATION,
	syncGetCurrentSettings,
} from '../../../../environmentVariables/EnvironmentVariables';
import { AuctionToastEvent } from '../../../../utils/DriftAppEvents';
import {
	getAdditionalEndPriceBuffer,
	isForceAuctionEndPriceUpToSlippage,
} from 'src/utils/slippage';
import { generateUserOrderIdForUser } from 'src/utils/order';
import useMarketStateStore, {
	DerivedMarketTradeState,
} from 'src/stores/useMarketStateStore';
import { dlog } from 'src/dev';
import { SignedMsgToastEvent } from 'src/@types/signedMsgOrders';
import posthog from 'posthog-js';
import useDriftAccountStore from '../../../useDriftAccountsStore';
import {
	getOrderParams,
	type ProcessedOrderParamsResponse,
} from 'src/utils/orderParamsClient';
import { SlippageTolerance } from 'src/@types/types';
import { buildLimitAuctionParams } from 'src/components/TradeForm/utils/buildLimitAuctionParams';
import {
	CommonTradeFormOutputOrderConstructionProps,
	PerpTradeFormOutputProps,
} from './types';
import { TRADE_PREP_UTILS } from './tradePrep';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

// Toast properties for perp order events
export interface PerpToastProps {
	auctionToastEventProps: AuctionToastEvent;
	signedMsgOrderToastEventProps: SignedMsgToastEvent;
	toastId: string;
}

// Toast properties for spot order events
export interface SpotToastProps {
	auctionToastEventProps: AuctionToastEvent;
	toastId: string;
}

// Combined return type for order params and toast props (perp)
export interface PerpOrderParamsWithToastProps {
	paramsResponse: ProcessedOrderParamsResponse;
	toastProps: PerpToastProps;
	numOrders: number;
	userOrderId: number;
}

// Combined return type for order params and toast props (spot)
export interface SpotOrderParamsWithToastProps {
	paramsResponse: ProcessedOrderParamsResponse;
	toastProps: SpotToastProps;
	userOrderId: number;
}

// Strict interface to reduce noise out of params which the SDK asks for but don't actually get used
export interface StrictPerpMarketOrderParams {
	reduceOnly: boolean;
	marketIndex: number;
	marketType: MarketType;
	baseAssetAmount: BN;
	direction: PositionDirection;
	orderType: OrderType;
	auctionDuration: number | null;
	auctionStartPrice: BN | null;
	auctionEndPrice: BN | null;
	oraclePriceOffset: number | null;
	maxTs: BN | null;
	userOrderId: number;
	// Price can be undefined when oraclePriceOffset is set
	price: BN | undefined;
}

// Strict interfaces for spot orders to match perp pattern
export interface StrictSpotBasicMarketOrderParams {
	reduceOnly: boolean;
	orderType: OrderType;
	marketIndex: number;
	baseAssetAmount: BN;
	direction: PositionDirection;
	marketType: MarketType;
	price: BN;
	auctionDuration: number | null;
	auctionStartPrice: BN | null;
	auctionEndPrice: BN | null;
	oraclePriceOffset: number | null;
	userOrderId: number;
}

export interface StrictSpotOracleMarketOrderParams {
	reduceOnly: boolean;
	orderType: OrderType;
	marketIndex: number;
	baseAssetAmount: BN;
	direction: PositionDirection;
	marketType: MarketType;
	auctionDuration: number | null;
	auctionStartPrice: BN | null;
	auctionEndPrice: BN | null;
	oraclePriceOffset: number | null;
	userOrderId: number;
}

export interface SpotTradeFormOutputProps extends CommonTradeFormOutputOrderConstructionProps {
	targetMarketIndex: number;
	currentPositionBaseSize: BN;
	currentPositionDirection: 'long' | 'short';
	spotMarketAccount: SpotMarketAccount;
}

interface OrderParamsWithUserOrderId extends Omit<
	OptionalOrderParams,
	'userOrderId'
> {
	userOrderId: number;
}

// These are the params actually passed to the Drift Client's method to prepare the market order
export type FinalPreppedMarketOrderProps = {
	orderParams: OrderParamsWithUserOrderId;
	userAccountPublicKey: PublicKey;
	user: User;
	userAccount: UserAccount;
	makerInfo?: MakerInfo | MakerInfo[];
	txParams?: TxParams;
	bracketOrdersParams: OptionalOrderParams[];
	referrerInfo: ReferrerInfo;
	cancelExistingOrders?: boolean;
	settlePnl?: boolean;
	auctionToastEventProps: AuctionToastEvent;
	signedMsgOrderToastEventProps: SignedMsgToastEvent;
	entryPrice: BN;
	oraclePrice: BN;
	nextOrderId: number;
	subAccountId: number;
	attemptPlaceAndTake: boolean;
	numOrders: number;
	toastId: string;
	marketName: string;
};

export type OrderPrepHarnessProps = {
	getUserAccountData: () => {
		client: User | undefined;
		pubKey: PublicKey;
	};
	getDriftClient: () => DriftClient | undefined;
	getPriceInfo: (marketId: MarketId) => DerivedMarketTradeState;
	getOraclePriceData: (marketId: MarketId) => BN;
};

export const getNextUserOrderIdForCurrentUser = () => {
	const currentUser =
		useDriftAccountStore.getState().accounts[
			useDriftAccountStore.getState().currentUserKey ?? ''
		]?.client; // TODO :: Consider making User an input prop;

	if (!currentUser) {
		return 0; // Handle potential edge cases where we are generating an order before there is a current user initialized
	}

	return generateUserOrderIdForUser(
		useDriftAccountStore.getState().accounts[
			useDriftAccountStore.getState().currentUserKey ?? ''
		]?.client as User // TODO :: Consider making User an input prop
	);
};

const processPerpTradeFormOutputProps = async (
	inputProps: PerpTradeFormOutputProps,
	harnessProps: OrderPrepHarnessProps
): Promise<PerpOrderParamsWithToastProps> => {
	const nextUserOrderId = getNextUserOrderIdForCurrentUser();

	const direction =
		inputProps.side === 'buy'
			? PositionDirection.LONG
			: PositionDirection.SHORT;

	const parsedBaseAmount =
		TRADE_PREP_UTILS.perp.formatTradeFormFieldValue.baseSize(
			inputProps.baseSizeStringValue as string
		);
	const roundedBaseAmount = TRADE_PREP_UTILS.perp.adjustForStepSize(
		parsedBaseAmount,
		inputProps.perpMarketAccount
	);

	const marketId = MarketId.createPerpMarket(
		inputProps.perpMarketAccount.marketIndex
	);

	const numOrders = inputProps.bracketOrders
		? inputProps.bracketOrders.stopLoss && inputProps.bracketOrders.takeProfit
			? 3
			: inputProps.bracketOrders.stopLoss || inputProps.bracketOrders.takeProfit
				? 2
				: 1
		: 1;

	const currentSettings =
		inputProps?.currentSettings ?? syncGetCurrentSettings();

	const auctionToastEventProps = TRADE_PREP_UTILS.getAuctionToastEventProps({
		marketId,
		direction,
		baseSize: roundedBaseAmount,
		numOrders,
		currentSettings: currentSettings as Partial<UserSettings>,
		bracketOrders: inputProps.bracketOrders,
		orderType: inputProps.orderType,
		userOrderId: nextUserOrderId,
	});

	const signedMsgOrderToastEventProps =
		TRADE_PREP_UTILS.getSignedMsgOrderToastEventProps({
			marketId,
			direction,
			baseSize: roundedBaseAmount,
			numOrders,
			currentSettings: currentSettings as Partial<UserSettings>,
			bracketOrders: inputProps.bracketOrders,
			orderType: inputProps.orderType,
			userOrderId: nextUserOrderId,
		});

	const toastId = TRADE_PREP_UTILS.getToastId(
		marketId,
		direction,
		inputProps.orderType as UIOrderType
	);

	const forceAuctionEndPriceUpToSlippage = isForceAuctionEndPriceUpToSlippage(
		inputProps.slippageTolerance as SlippageTolerance,
		marketId
	);

	const driftClient = harnessProps.getDriftClient();

	// Log order params request for posthog analytics
	const userAccountPubkey = harnessProps
		.getUserAccountData()
		?.pubKey.toString();

	const orderParams = await getOrderParams({
		assetType: 'base',
		marketType: MarketType.PERP,
		marketIndex: marketId.marketIndex,
		direction,
		maxLeverageSelected: inputProps.maxLeverageSelected || false,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		amount: roundedBaseAmount.val.abs(),
		reduceOnly: inputProps.reduceOnly || false,
		auctionDuration:
			currentSettings?.auctionDuration ?? DEFAULT_MARKET_AUCTION_DURATION,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset as number | 'marketBased',
				marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset ?? 0,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom as
					| TradeOffsetPrice
					| 'marketBased',
				marketId
			),
		auctionEndPriceOffsetFrom:
			currentSettings?.auctionEndPriceOffsetFrom ?? 'marketBased',
		auctionPriceCaps: undefined,
		slippageTolerance: inputProps.slippageTolerance as SlippageTolerance,
		isOracleOrder: currentSettings?.oracleOffsetOrdersEnabled ?? false,
		additionalEndPriceBuffer: getAdditionalEndPriceBuffer(
			inputProps.perpMarketAccount
		),
		forceUpToSlippage: forceAuctionEndPriceUpToSlippage,
		// Add fallback parameters
		driftClient: driftClient as DriftClient,
		// Enable PostHog logging for this call
		enablePosthogLogging: true,
		userAccountPubKey: userAccountPubkey,
		orderType: inputProps.orderType as 'market' | 'oracle',
		userOrderId: nextUserOrderId,
	});

	return {
		paramsResponse: orderParams,
		toastProps: {
			auctionToastEventProps,
			signedMsgOrderToastEventProps,
			toastId,
		},
		numOrders,
		userOrderId: nextUserOrderId,
	};
};

const getBracketOrdersParams = ({
	bracketOrders,
	marketIndex,
	baseAmount,
	direction,
}: {
	bracketOrders: CommonTradeFormOutputOrderConstructionProps['bracketOrders'];
	marketIndex: number;
	baseAmount: BN;
	direction: PositionDirection;
}) => {
	if (bracketOrders) {
		const bracketOrdersParams = [];

		if (bracketOrders.takeProfit) {
			const takeProfitParams = getTriggerMarketOrderParams({
				marketIndex,
				marketType: MarketType.PERP,
				direction: bracketOrders.takeProfit.direction,
				baseAssetAmount: baseAmount,
				triggerPrice: bracketOrders.takeProfit.price?.val as BN,
				triggerCondition: isVariant(direction, 'short')
					? OrderTriggerCondition.BELOW
					: OrderTriggerCondition.ABOVE,
				reduceOnly: true,
			});

			bracketOrdersParams.push(takeProfitParams);
		}

		if (bracketOrders.stopLoss) {
			const stopLossParams = getTriggerMarketOrderParams({
				marketIndex,
				marketType: MarketType.PERP,
				direction: bracketOrders.stopLoss.direction,
				baseAssetAmount: baseAmount,
				triggerPrice: bracketOrders.stopLoss.price?.val as BN,
				triggerCondition: isVariant(direction, 'short')
					? OrderTriggerCondition.ABOVE
					: OrderTriggerCondition.BELOW,
				reduceOnly: true,
			});

			bracketOrdersParams.push(stopLossParams);
		}

		return bracketOrdersParams;
	}

	return undefined;
};

/**
 * Takes the output from the tradeform and some necessary harness stuff, outputs the parameters we can pass into the DriftClient's market order method.
 * @param inputProps
 * @param harnessProps
 * @returns
 */
const prepPerpMarketOrderParams = async (
	inputProps: PerpTradeFormOutputProps,
	harnessProps: OrderPrepHarnessProps,
	userDataOverride?: {
		accountDoesNotExistYet: boolean;
		pubKey: PublicKey;
		nextOrderId: number;
		subAccountId: number;
	}
): Promise<FinalPreppedMarketOrderProps> => {
	try {
		// Intentionally reads from store for diagnostics — logs store state at order time
		const FORCE_LOG_INSTEAD_OF_POSTHOG = false;
		const CUTOFF_PRICE_DIFF_DEBUG_PCT = 2; // This is the price difference (in percent) between oracle and mark where we want to log the state of the app to determine the cause of the UI bug creating large wicks
		const marketId = MarketId.createPerpMarket(inputProps.targetMarketIndex);
		const oraclePrice = harnessProps.getOraclePriceData(marketId);
		const markPrice =
			harnessProps.getPriceInfo(marketId)?.markPrice?.val ?? ZERO;
		const absPriceDiff = markPrice.sub(oraclePrice).abs();
		if (
			FORCE_LOG_INSTEAD_OF_POSTHOG ||
			absPriceDiff.gt(oraclePrice.divn(100).muln(CUTOFF_PRICE_DIFF_DEBUG_PCT))
		) {
			// NOTE: it doesn't actually matter anymore if this data is stale since orderParamsClient will always re-fetch
			// not sure if it's being used for anything but can probably remove
			const eventData = {
				orderType: inputProps.orderType,
				market: marketId.key,
				markPrice: markPrice.toNumber() / 1e6,
				oraclePrice: oraclePrice.toNumber() / 1e6,
				absPriceDiff: absPriceDiff.toNumber() / 1e6,
				subscriptionState: useMarketStateStore
					.getState()
					.getSubscriptionStateForMarket(marketId),
				topBidInMarketStateStore:
					useMarketStateStore
						.getState()
						.getDlobStateForMarket(marketId)
						.bids[0]?.price.toNumber() / 1e6,
				topAskInMarketStateStore:
					useMarketStateStore
						.getState()
						.getDlobStateForMarket(marketId)
						.asks[0]?.price.toNumber() / 1e6,
				markPriceInMarketStateStore:
					useMarketStateStore
						.getState()
						.getMarkPriceForMarket(marketId)
						?.toNum() ?? 0,
				oraclePriceInMarketStateStore:
					useMarketStateStore
						.getState()
						.getOracleDataForMarket(marketId)
						?.price.toNum() ?? 0,
				userSettings: inputProps.currentSettings,
				swiftEnabled: inputProps.isSwiftEnabled,
			};

			if (FORCE_LOG_INSTEAD_OF_POSTHOG) {
				dlog('oracle_mark_divergence', 'eventData', eventData);
			} else {
				posthog.capture('oracle_mark_divergence', eventData);
			}
		}
	} catch (e) {
		console.error(
			`Error sending posthog event during perp market order param prep`,
			e
		);
	}

	const driftClient = harnessProps.getDriftClient();
	const userData = harnessProps.getUserAccountData();
	const processedProps = await processPerpTradeFormOutputProps(
		inputProps,
		harnessProps
	);
	const currentSettings =
		inputProps?.currentSettings ?? syncGetCurrentSettings();

	// Get oracle price for tracking
	const marketId = MarketId.createPerpMarket(inputProps.targetMarketIndex);
	const oraclePrice = harnessProps.getOraclePriceData(marketId);

	// Round auction prices to tick size
	const tickSize = inputProps.perpMarketAccount.amm.orderTickSize;

	const roundedAuctionStartPrice = processedProps.paramsResponse.auctionParams
		.auctionStartPrice
		? processedProps.paramsResponse.auctionParams.auctionStartPrice
				.div(tickSize)
				.mul(tickSize)
		: undefined;

	const roundedAuctionEndPrice = processedProps.paramsResponse.auctionParams
		.auctionEndPrice
		? processedProps.paramsResponse.auctionParams.auctionEndPrice
				.div(tickSize)
				.mul(tickSize)
		: undefined;

	const roundedOraclePriceOffset = processedProps.paramsResponse.auctionParams
		.oraclePriceOffset
		? processedProps.paramsResponse.auctionParams.oraclePriceOffset
				.div(tickSize)
				.mul(tickSize)
		: undefined;

	// Create a modified params response with rounded auction prices
	const roundedParamsResponse = {
		...processedProps.paramsResponse,
		auctionParams: {
			...processedProps.paramsResponse.auctionParams,
			auctionStartPrice: roundedAuctionStartPrice,
			auctionEndPrice: roundedAuctionEndPrice,
			oraclePriceOffset: roundedOraclePriceOffset,
		},
	};

	const attemptPlaceAndTake =
		driftClient?.txVersion === 0 && currentSettings?.placeAndTakeEnabled;

	const orderParams = getStrictPerpMarketOrderParams(
		roundedParamsResponse,
		processedProps.userOrderId
	);

	// Check if high leverage spots are available
	const highLeverageSpots = useDriftStore.getState().highLeverageSpots;

	const bitFlags =
		inputProps.reduceOnly ||
		!driftClient ||
		!userData?.client ||
		highLeverageSpots.spotsOpen <= 0
			? undefined
			: COMMON_UI_UTILS.getPerpOrderParamsBitFlags(
					inputProps.targetMarketIndex,
					driftClient,
					userData.client,
					inputProps.displayLeverage,
					{
						numOfOpenHighLeverageSpots: highLeverageSpots.spotsOpen,
					}
				);

	const marketName = UIMarket.perpMarkets.find(
		(market) => market.marketIndex === inputProps.targetMarketIndex
	)?.symbol;

	const output: FinalPreppedMarketOrderProps = {
		orderParams: {
			...orderParams,
			bitFlags,
		},
		userAccountPublicKey: userDataOverride?.pubKey ?? userData.pubKey,
		userAccount: userDataOverride
			? (undefined as unknown as UserAccount) // heinous type error to fill null check without dangerously altering logic
			: (userData.client?.getUserAccount() as UserAccount),
		user: userDataOverride
			? (undefined as unknown as User)
			: (userData.client as User),
		cancelExistingOrders: inputProps.cancelExistingOrders,
		settlePnl: orderParams.reduceOnly,
		auctionToastEventProps: processedProps.toastProps.auctionToastEventProps,
		signedMsgOrderToastEventProps:
			processedProps.toastProps.signedMsgOrderToastEventProps,
		entryPrice: roundedParamsResponse.priceImpact.entryPrice,
		oraclePrice,
		nextOrderId:
			userDataOverride?.nextOrderId ??
			(userData.client?.getUserAccount().nextOrderId as number),
		subAccountId:
			userDataOverride?.subAccountId ??
			(userData.client?.getUserAccount().subAccountId as number),
		attemptPlaceAndTake: attemptPlaceAndTake as boolean,
		numOrders: processedProps.numOrders,
		toastId: processedProps.toastProps.toastId,
		referrerInfo: userDataOverride?.accountDoesNotExistYet
			? (undefined as unknown as ReferrerInfo) // heinous type error to fill null check without dangerously altering logic
			: (driftClient?.getUserStats()?.getReferrerInfo() as ReferrerInfo),
		marketName: marketName as string,
		bracketOrdersParams: getBracketOrdersParams({
			bracketOrders: inputProps.bracketOrders,
			marketIndex: inputProps.targetMarketIndex,
			baseAmount: roundedParamsResponse.auctionParams.baseAssetAmount,
			direction: roundedParamsResponse.auctionParams.direction,
		}) as OptionalOrderParams[],
	};

	return output;
};

// ### SPOT

interface OrderParamsWithUserOrderId extends OptionalOrderParams {
	userOrderId: number;
}

export interface FinalPreppedSpotMarketOrderProps {
	orderParams: OrderParamsWithUserOrderId;
	userAccountPublicKey: PublicKey;
	user: User;
	userAccount: UserAccount;
	auctionToastEventProps: AuctionToastEvent;
	entryPrice: BN;
	oraclePrice: BN;
	nextOrderId: number;
	subAccountId: number;
	attemptPlaceAndTake: boolean;
	toastId: string;
	referrerInfo: ReferrerInfo;
	marketName: string;
}

const prepSpotMarketOrderParams = async (
	inputProps: SpotTradeFormOutputProps,
	harnessProps: OrderPrepHarnessProps
): Promise<FinalPreppedSpotMarketOrderProps> => {
	const driftClient = harnessProps.getDriftClient();
	const userData = harnessProps.getUserAccountData();
	const processedProps = await processSpotTradeFormOutput(
		inputProps,
		harnessProps
	);
	const currentSettings =
		inputProps?.currentSettings ?? syncGetCurrentSettings();

	// Get oracle price for tracking
	const marketId = MarketId.createSpotMarket(inputProps.targetMarketIndex);
	const oraclePrice = harnessProps.getOraclePriceData(marketId);

	const isOracleOrder = currentSettings?.oracleOffsetOrdersEnabled ?? false;
	const orderParams = isOracleOrder
		? getStrictSpotOracleOrderParams(
				processedProps.paramsResponse,
				processedProps.userOrderId
			)
		: getStrictSpotMarketOrderParams(
				processedProps.paramsResponse,
				processedProps.userOrderId
			);

	const attemptPlaceAndTake =
		driftClient?.txVersion === 0 && currentSettings?.placeAndTakeEnabled;

	const targetMarket = UIMarket.spotMarkets.find(
		(market) => market.marketIndex === inputProps.targetMarketIndex
	);

	const marketName = targetMarket?.symbol;

	return {
		orderParams,
		userAccountPublicKey: userData.pubKey,
		userAccount: userData.client?.getUserAccount() as UserAccount,
		auctionToastEventProps: processedProps.toastProps.auctionToastEventProps,
		entryPrice: processedProps.paramsResponse.priceImpact.entryPrice,
		oraclePrice,
		nextOrderId: userData.client?.getUserAccount().nextOrderId as number,
		subAccountId: userData.client?.getUserAccount().subAccountId as number,
		user: userData.client as User,
		attemptPlaceAndTake: attemptPlaceAndTake as boolean,
		toastId: processedProps.toastProps.toastId,
		referrerInfo: driftClient
			?.getUserStats()
			?.getReferrerInfo() as ReferrerInfo,
		marketName: marketName as string,
	};
};

const processSpotTradeFormOutput = async (
	inputProps: SpotTradeFormOutputProps,
	harnessProps: OrderPrepHarnessProps
): Promise<SpotOrderParamsWithToastProps> => {
	const direction =
		inputProps.side === 'buy'
			? PositionDirection.LONG
			: PositionDirection.SHORT;

	const formattedBaseAmount =
		TRADE_PREP_UTILS.spot.formatTradeFormFieldValue.baseSize(
			inputProps.baseSizeStringValue as string,
			inputProps.spotMarketAccount
		);

	const currentSettings =
		inputProps?.currentSettings ?? syncGetCurrentSettings();

	const marketId = MarketId.createSpotMarket(inputProps.targetMarketIndex);

	const nextUserOrderId = getNextUserOrderIdForCurrentUser();

	const auctionToastEventProps: AuctionToastEvent = {
		v2Props: {
			marketId: MarketId.createSpotMarket(inputProps.targetMarketIndex),
			direction,
			baseAmountOrdered: formattedBaseAmount.val,
			numOrders: 1,
			auctionEnabled: false,
			includesSlOrder: false,
			includesTpOrder: false,
			userOrderId: nextUserOrderId,
		},
		toastId: TRADE_PREP_UTILS.getToastId(marketId, direction, 'market'),
	};

	const toastId = TRADE_PREP_UTILS.getToastId(marketId, direction, 'market');

	const forceAuctionEndPriceUpToSlippage = isForceAuctionEndPriceUpToSlippage(
		inputProps.slippageTolerance as SlippageTolerance,
		marketId
	);

	// Create getter functions for fallback parameters
	const driftClient = harnessProps.getDriftClient();

	const orderParams = await getOrderParams({
		assetType: 'base',
		marketType: MarketType.SPOT,
		marketIndex: marketId.marketIndex,
		direction,
		maxLeverageSelected: inputProps.maxLeverageSelected || false,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		amount: formattedBaseAmount.val.abs(),
		reduceOnly: inputProps.reduceOnly || false,
		auctionDuration:
			currentSettings?.auctionDuration ?? DEFAULT_MARKET_AUCTION_DURATION,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset as number,
				marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset as number,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom as TradeOffsetPrice,
				marketId
			),
		auctionEndPriceOffsetFrom:
			currentSettings?.auctionEndPriceOffsetFrom as TradeOffsetPrice,
		slippageTolerance: inputProps.slippageTolerance as SlippageTolerance,
		isOracleOrder: currentSettings?.oracleOffsetOrdersEnabled ?? false,
		additionalEndPriceBuffer: ZERO,
		forceUpToSlippage: forceAuctionEndPriceUpToSlippage,
		// Add fallback parameters
		driftClient: driftClient as DriftClient,
		orderType: inputProps.orderType as 'market' | 'oracle',
	});

	return {
		paramsResponse: orderParams,
		toastProps: {
			auctionToastEventProps,
			toastId,
		},
		userOrderId: nextUserOrderId,
	};
};

const getStrictPerpMarketOrderParams = (
	paramsResponse: ProcessedOrderParamsResponse,
	userOrderId: number
): StrictPerpMarketOrderParams => {
	return {
		reduceOnly: paramsResponse.auctionParams.reduceOnly ?? false,
		marketIndex: paramsResponse.auctionParams.marketIndex,
		baseAssetAmount: paramsResponse.auctionParams.baseAssetAmount,
		direction: paramsResponse.auctionParams.direction,
		marketType: MarketType.PERP,
		orderType: paramsResponse.auctionParams.orderType,
		auctionDuration: paramsResponse.auctionParams.auctionDuration ?? null,
		auctionStartPrice: paramsResponse.auctionParams.auctionStartPrice ?? null,
		auctionEndPrice: paramsResponse.auctionParams.auctionEndPrice ?? null,
		price: paramsResponse.auctionParams.oraclePriceOffset
			? undefined
			: (paramsResponse.auctionParams.auctionEndPrice ?? undefined),
		oraclePriceOffset:
			paramsResponse.auctionParams.oraclePriceOffset?.toNumber() ?? null,
		maxTs: paramsResponse.auctionParams.maxTs ?? null,
		userOrderId,
	};
};

const getStrictSpotMarketOrderParams = (
	paramsResponse: ProcessedOrderParamsResponse,
	userOrderId: number
): StrictSpotBasicMarketOrderParams => {
	return {
		price: paramsResponse.auctionParams.auctionEndPrice ?? ZERO,
		reduceOnly: paramsResponse.auctionParams.reduceOnly ?? false,
		orderType: paramsResponse.auctionParams.orderType,
		marketIndex: paramsResponse.auctionParams.marketIndex,
		baseAssetAmount: paramsResponse.auctionParams.baseAssetAmount,
		direction: paramsResponse.auctionParams.direction,
		marketType: MarketType.SPOT,
		auctionDuration: paramsResponse.auctionParams.auctionDuration ?? null,
		auctionStartPrice: paramsResponse.auctionParams.auctionStartPrice ?? null,
		auctionEndPrice: paramsResponse.auctionParams.auctionEndPrice ?? null,
		oraclePriceOffset:
			paramsResponse.auctionParams.oraclePriceOffset?.toNumber() ?? null,
		userOrderId,
	};
};

const getStrictSpotOracleOrderParams = (
	paramsResponse: ProcessedOrderParamsResponse,
	userOrderId: number
): StrictSpotOracleMarketOrderParams => {
	return {
		reduceOnly: paramsResponse.auctionParams.reduceOnly ?? false,
		orderType: paramsResponse.auctionParams.orderType,
		marketIndex: paramsResponse.auctionParams.marketIndex,
		baseAssetAmount: paramsResponse.auctionParams.baseAssetAmount,
		direction: paramsResponse.auctionParams.direction,
		marketType: MarketType.SPOT,
		auctionDuration: paramsResponse.auctionParams.auctionDuration ?? null,
		auctionStartPrice: paramsResponse.auctionParams.auctionStartPrice ?? null,
		auctionEndPrice: paramsResponse.auctionParams.auctionEndPrice ?? null,
		oraclePriceOffset:
			paramsResponse.auctionParams.oraclePriceOffset?.toNumber() ?? null,
		userOrderId,
	};
};

/**
 * Gets limit order auction params by fetching the calculated auction start price
 * from the order params client, then calling COMMON_UI_UTILS.getLimitAuctionParams
 */
const getLimitOrderAuctionParams = async ({
	marketId,
	limitPrice,
	direction,
	harnessProps,
	amount,
	maxLeverageSelected = false,
	reduceOnly = false,
	settings,
	slippageTolerance,
}: {
	marketId: MarketId;
	limitPrice: BigNum;
	direction: PositionDirection;
	harnessProps: OrderPrepHarnessProps;
	amount: BN;
	maxLeverageSelected?: boolean;
	reduceOnly?: boolean;
	settings?: Partial<UserSettings>;
	slippageTolerance: SlippageTolerance;
}): Promise<AuctionParams> => {
	const currentSettings = settings ?? syncGetCurrentSettings();

	// Create getter functions for fallback parameters
	const driftClient = harnessProps.getDriftClient();

	if (!driftClient) {
		throw new Error('DriftClient is required for getLimitOrderAuctionParams');
	}

	// Utilize the auction params calculator just to fetch what the auctionStartPrice WOULD be for a market order
	// If it is better than the limit price, we will use this for auction params
	const orderParams = await getOrderParams({
		assetType: 'base',
		marketType: marketId.marketType,
		marketIndex: marketId.marketIndex,
		direction,
		maxLeverageSelected,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		amount,
		reduceOnly,
		auctionDuration:
			currentSettings?.auctionDuration ?? DEFAULT_MARKET_AUCTION_DURATION,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset ?? 'marketBased',
				marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset ?? 0,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom ?? 'marketBased',
				marketId
			),
		auctionEndPriceOffsetFrom:
			currentSettings?.auctionEndPriceOffsetFrom ?? 'oracle',
		slippageTolerance,
		isOracleOrder: false,
		additionalEndPriceBuffer: marketId.isPerp
			? getAdditionalEndPriceBuffer(
					driftClient.getPerpMarketAccount(marketId.marketIndex)
				)
			: ZERO,
		forceUpToSlippage: false,
		orderType: 'market',
		driftClient,
	});

	// Use the calculated auction start price from the order params response
	const auctionStartPrice =
		orderParams.auctionParams?.auctionStartPrice ?? ZERO;

	// Use buildLimitAuctionParams with the new parameters
	return buildLimitAuctionParams({
		driftClient,
		direction,
		marketId,
		inputPrice: limitPrice,
		auctionStartPrice,
		oraclePrice: orderParams.oraclePrice,
	});
};

const getOptionalAuctionParamsInputs = ({
	maxLeverageSelected,
	uiMarket,
	slippageTolerance,
	overrideIsOracleOrder,
	marketAccount,
}: {
	maxLeverageSelected: boolean;
	uiMarket: UIMarket;
	slippageTolerance: SlippageTolerance;
	overrideIsOracleOrder?: {
		isOracleOrder: boolean;
	};
	marketAccount: PerpMarketAccount | SpotMarketAccount;
}): OptionalAuctionParamsRequestInputs => {
	const currentSettings = syncGetCurrentSettings();

	const additionalEndPriceBuffer = uiMarket.isPerp
		? getAdditionalEndPriceBuffer(marketAccount as PerpMarketAccount)
		: ZERO;

	return {
		maxLeverageSelected: maxLeverageSelected,
		maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
		auctionDuration:
			currentSettings?.auctionDuration ?? DEFAULT_MARKET_AUCTION_DURATION,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				currentSettings?.auctionStartPriceOffset as number | 'marketBased',
				uiMarket.marketId
			),
		auctionEndPriceOffset: currentSettings?.auctionEndPriceOffset ?? 0,
		auctionStartPriceOffsetFrom:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
				currentSettings?.auctionStartPriceOffsetFrom as
					| TradeOffsetPrice
					| 'marketBased',
				uiMarket.marketId
			),
		auctionEndPriceOffsetFrom: currentSettings?.auctionEndPriceOffsetFrom,
		slippageTolerance,
		isOracleOrder: overrideIsOracleOrder
			? overrideIsOracleOrder.isOracleOrder
			: (currentSettings?.oracleOffsetOrdersEnabled ?? false),
		additionalEndPriceBuffer,
		forceUpToSlippage: isForceAuctionEndPriceUpToSlippage(
			slippageTolerance,
			uiMarket.marketId
		),
	};
};

const getExtraInfoFromPerpTradeFormOutputProps = (
	perpTradeFormOutputProps: PerpTradeFormOutputProps & {
		leadSide: 'base' | 'quote';
	},
	overrideIsOracleOrder?: {
		isOracleOrder: boolean;
	}
) => {
	const perpMarketAccount = perpTradeFormOutputProps.perpMarketAccount;

	const direction =
		perpTradeFormOutputProps.side === 'buy'
			? PositionDirection.LONG
			: PositionDirection.SHORT;

	const parsedBaseAmount =
		TRADE_PREP_UTILS.perp.formatTradeFormFieldValue.baseSize(
			perpTradeFormOutputProps.baseSizeStringValue ?? '0'
		);
	const baseAmountBigNum = TRADE_PREP_UTILS.perp.adjustForStepSize(
		parsedBaseAmount,
		perpMarketAccount
	);

	const bracketOrders: {
		takeProfit?: OptionalTriggerOrderParams;
		stopLoss?: OptionalTriggerOrderParams;
	} = {};

	if (perpTradeFormOutputProps.bracketOrders?.takeProfit) {
		const takeProfitParams = perpTradeFormOutputProps.bracketOrders.takeProfit;

		bracketOrders.takeProfit = {
			triggerPrice: takeProfitParams.price.val,
			baseAssetAmount: baseAmountBigNum.val,
			reduceOnly: true,
		};
	}
	if (perpTradeFormOutputProps.bracketOrders?.stopLoss) {
		const stopLossParams = perpTradeFormOutputProps.bracketOrders.stopLoss;

		bracketOrders.stopLoss = {
			triggerPrice: stopLossParams.price.val,
			baseAssetAmount: baseAmountBigNum.val,
			reduceOnly: true,
		};
	}

	const numOfOrders =
		1 + (bracketOrders.takeProfit ? 1 : 0) + (bracketOrders.stopLoss ? 1 : 0);

	const uiMarket = UIMarket.createPerpMarket(
		perpTradeFormOutputProps.targetMarketIndex
	);
	const optionalAuctionParamsInputs = getOptionalAuctionParamsInputs({
		maxLeverageSelected: !!perpTradeFormOutputProps.maxLeverageSelected,
		uiMarket,
		slippageTolerance:
			perpTradeFormOutputProps.slippageTolerance as SlippageTolerance,
		marketAccount: perpMarketAccount,
		overrideIsOracleOrder: overrideIsOracleOrder,
	});

	return {
		direction,
		baseAmountBigNum,
		bracketOrders,
		numOfOrders,
		optionalAuctionParamsInputs,
	};
};

export const ORDER_PREP_UTILS = {
	prepPerpMarketOrderParams,
	prepSpotMarketOrderParams,
	getLimitOrderAuctionParams,
	getOptionalAuctionParamsInputs,
	getExtraInfoFromPerpTradeFormOutputProps,
	getNextUserOrderIdForCurrentUser,
};
